/**
 * Workshop SlackFrame — channel view + follow-ups.
 *
 * Props-in, JSX-out. Thread rows and links emit prompt text via
 * onFollowUp, follow-up chips do the same; nothing executes.
 */

import type { ReactNode } from "react";
import { useState } from "react";
import { Icons } from "@/components/ui/icons";
import {
  surfaceRoleAttributes,
  surfaceRoleClassName,
} from "@/components/ui";
import { cn } from "@/lib/cn";
import type { FrameStatus } from "@/workshop/components/shapes/shared/frame-utils";

interface SlackFrameMessage {
  id: string;
  user: string;
  initials: string;
  color: string;
  time: string;
  text: string;
  threadReplies?: number;
}

interface SlackFrameProps {
  channel?: string;
  messages?: SlackFrameMessage[];
  status?: FrameStatus;
  onFollowUp?: (text: string) => void;
  className?: string;
  "data-testid"?: string;
}

const DEFAULT_MESSAGES: SlackFrameMessage[] = [
  {
    id: "s1",
    user: "Mara Jensen",
    initials: "MJ",
    color: "#7c5cf0",
    time: "09:41",
    text: "Morning team — the *launch checklist* is green except App Store screenshots. Details in <https://example.com/checklist|the tracker>.",
  },
  {
    id: "s2",
    user: "Jonas Weber",
    initials: "JW",
    color: "#2fa98c",
    time: "09:45",
    text: "Review usually takes under 24h for updates, so *tomorrow's submission* is safe. Screenshots are the only blocker.",
    threadReplies: 3,
  },
  {
    id: "s3",
    user: "Priya Nair",
    initials: "PN",
    color: "#e07a5f",
    time: "09:52",
    text: "Design will land the screenshots by *EOD today*. I'll drop them in <https://example.com/assets|the asset folder> when ready.",
  },
];

/** Keep per-user avatar colors safe for inline use; else neutral gray. */
function safeAvatarColor(color: string): string {
  return /^#[0-9a-fA-F]{3,8}$/.test(color) ? color : "#616061";
}

function safeInitials(initials: string): string {
  return initials.replace(/[^a-zA-Z0-9]/g, "").slice(0, 3) || "?";
}

/**
 * Minimal mrkdwn renderer (*bold*, `code`, <url|label>) as React nodes —
 * links emit follow-up prompts instead of navigating, so nothing in the
 * workshop ever leaves the page.
 */
function renderMrkdwn(
  text: string,
  testId: string,
  onFollowUp?: (text: string) => void,
): ReactNode[] {
  const parts = text.split(
    /(\*[^*\n]+\*|<https?:\/\/[^<>\s|]+(?:\|[^<>]+)?>|`[^`\n]+`)/g,
  );
  return parts.map((part, index) => {
    if (part.startsWith("*") && part.endsWith("*") && part.length > 2) {
      return (
        <strong key={index} className="font-semibold text-primary">
          {part.slice(1, -1)}
        </strong>
      );
    }
    if (part.startsWith("`") && part.endsWith("`") && part.length > 2) {
      return (
        <code
          key={index}
          className="rounded bg-glass-fill-dense px-1 font-mono text-canvas-xs text-primary"
        >
          {part.slice(1, -1)}
        </code>
      );
    }
    const link = part.match(/^<(https?:\/\/[^<>\s|]+)(?:\|([^<>]+))?>$/);
    if (link) {
      const href = link[1] ?? part;
      const label = link[2] ?? href;
      return (
        <a
          key={index}
          href={href}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onFollowUp?.(`Open link: ${href}`);
          }}
          className="text-accent hover:underline"
          data-testid={`${testId}-link`}
        >
          {label}
        </a>
      );
    }
    return <span key={index}>{part}</span>;
  });
}

export function SlackFrame({
  channel = "launch-plan",
  messages = DEFAULT_MESSAGES,
  status = "done",
  onFollowUp,
  className,
  "data-testid": testId = "slack-frame",
}: SlackFrameProps) {
  const [errorDismissed, setErrorDismissed] = useState(false);
  const showError = status === "error" && !errorDismissed;
  const isEmpty = messages.length === 0;

  return (
    <div
      className={cn(
        surfaceRoleClassName("frame"),
        "group relative flex flex-col overflow-hidden rounded-frame",
        className,
      )}
      {...surfaceRoleAttributes("frame")}
      data-testid={testId}
    >
      <div className="flex min-h-0 flex-1 flex-col gap-1.5 overflow-y-auto px-2.5 py-2">
        {showError ? (
          <div
            className="flex flex-col gap-1.5 rounded-lg border border-danger/20 bg-danger-muted px-2.5 py-2"
            data-testid={`${testId}-error`}
          >
            <div className="flex items-center gap-1.5">
              <Icons.XCircle className="h-3.5 w-3.5 shrink-0 text-danger" />
              <span className="text-canvas-sm font-medium text-danger">
                Couldn&apos;t load channel
              </span>
            </div>
            <p className="text-canvas-sm leading-relaxed text-secondary">
              Message history failed to sync. Your follow-up prompts below
              still work.
            </p>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setErrorDismissed(true);
              }}
              className="self-start rounded-full border border-glass-stroke-subtle bg-glass-fill px-2 py-0.5 text-canvas-xs text-muted transition-colors hover:text-secondary"
              data-testid={`${testId}-error-dismiss`}
            >
              Dismiss
            </button>
          </div>
        ) : isEmpty ? (
          status === "streaming" ? (
            <div
              className="flex items-start gap-2 py-1"
              data-testid={`${testId}-typing`}
            >
              <div className="h-7 w-7 shrink-0 animate-pulse rounded-md bg-glass-fill-dense" />
              <div className="flex flex-1 flex-col gap-1.5 pt-0.5">
                <div className="h-2.5 w-24 animate-pulse rounded bg-glass-fill-dense" />
                <div className="h-2.5 w-3/4 animate-pulse rounded bg-glass-fill-dense" />
                <span className="sr-only">Loading messages</span>
              </div>
            </div>
          ) : (
            <div
              className="flex flex-1 flex-col items-center justify-center gap-1 py-6"
              data-testid={`${testId}-empty`}
            >
              <Icons.Hash className="h-5 w-5 text-subtle" />
              <p className="text-canvas-sm italic text-muted">
                No messages in #{channel} yet
              </p>
            </div>
          )
        ) : (
          messages.map((msg, idx) => (
            <div
              key={msg.id}
              data-testid={`${testId}-msg-${msg.id}`}
              className="flex min-w-0 shrink-0 gap-2 rounded-preview bg-elevated/60 px-3 py-2"
            >
              <span
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-canvas-2xs font-bold text-white"
                style={{ backgroundColor: safeAvatarColor(msg.color) }}
                aria-hidden="true"
              >
                {safeInitials(msg.initials)}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-1.5">
                  <span className="truncate text-canvas-sm font-semibold text-primary">
                    {msg.user}
                  </span>
                  <span className="shrink-0 text-canvas-xs text-muted">
                    {msg.time}
                  </span>
                </div>
                <p className="break-words text-canvas-sm leading-relaxed text-secondary">
                  {renderMrkdwn(msg.text, testId, onFollowUp)}
                  {idx === messages.length - 1 && status === "streaming" && (
                    <span
                      className="ml-1 inline-block h-2.5 w-0.5 animate-pulse rounded-sm bg-accent align-middle"
                      aria-label="Streaming"
                    />
                  )}
                </p>
                {typeof msg.threadReplies === "number" &&
                  msg.threadReplies > 0 && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onFollowUp?.(
                          `Open thread with ${msg.threadReplies} replies`,
                        );
                      }}
                      className="mt-1 inline-flex items-center gap-1 rounded-control bg-transparent px-1.5 py-0.5 text-canvas-xs text-muted transition-colors hover:bg-glass-stroke-faint/20 hover:text-secondary"
                      data-testid={`${testId}-thread-${msg.id}`}
                    >
                      <Icons.MessageCircle
                        className="h-3 w-3"
                        aria-hidden="true"
                      />
                      {msg.threadReplies}{" "}
                      {msg.threadReplies === 1 ? "reply" : "replies"}
                    </button>
                  )}
              </div>
            </div>
          ))
        )}

      </div>
    </div>
  );
}

export type {
  SlackFrameProps,
  SlackFrameMessage,
  FrameStatus as SlackFrameStatus,
};
